import type { ReactNode } from 'react';
import { Info, Lightbulb, AlertTriangle, Landmark, Languages } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

/**
 * Bilingual callout — DESIGN.md "Risk 2: Korean copy as design feature".
 *
 * The Korean subtitle is not a translation fallback; it renders at every
 * breakpoint alongside the English, in Noto Sans KR against Plus Jakarta Sans,
 * because the pairing is the trust signal.
 */

export type CalloutVariant = 'info' | 'tip' | 'warning' | 'legal' | 'language';

const VARIANTS: Record<
  CalloutVariant,
  { icon: typeof Info; wrap: string; chip: string; rule: string; label: string; labelKo: string }
> = {
  info: {
    icon: Info,
    wrap: 'bg-cheong-50 border-cheong-200 dark:bg-cheong-500/10 dark:border-cheong-dark/30',
    chip: 'bg-cheong-500 text-white dark:bg-cheong-dark',
    rule: 'bg-cheong-500 dark:bg-cheong-dark',
    label: 'Good to know',
    labelKo: '알아두기',
  },
  tip: {
    icon: Lightbulb,
    wrap: 'bg-hwang-50 border-hwang-200 dark:bg-hwang-500/10 dark:border-hwang-500/30',
    chip: 'bg-hwang-500 text-heuk-500',
    rule: 'bg-hwang-500',
    label: 'Insider tip',
    labelKo: '실전 팁',
  },
  warning: {
    icon: AlertTriangle,
    wrap: 'bg-jeok-50 border-jeok-200 dark:bg-jeok-500/10 dark:border-jeok-dark/30',
    chip: 'bg-jeok-500 text-white dark:bg-jeok-dark',
    rule: 'bg-jeok-500 dark:bg-jeok-dark',
    label: 'Watch out',
    labelKo: '주의하세요',
  },
  legal: {
    icon: Landmark,
    wrap: 'bg-ink-50 border-ink-200 dark:bg-surface-dark-card dark:border-surface-dark-border',
    chip: 'bg-ink-700 text-white dark:bg-ink-200 dark:text-ink-900',
    rule: 'bg-ink-700 dark:bg-ink-400',
    label: 'Official rule',
    labelKo: '공식 규정',
  },
  language: {
    icon: Languages,
    wrap: 'bg-ink-50 border-ink-200 dark:bg-surface-dark-card dark:border-surface-dark-border',
    chip: 'bg-cheong-500 text-white dark:bg-cheong-dark',
    rule: 'bg-cheong-500 dark:bg-cheong-dark',
    label: 'Say it in Korean',
    labelKo: '한국어로 말하기',
  },
};

interface BilingualCalloutProps {
  /** English heading — Plus Jakarta Sans. */
  title: string;
  /** Korean heading — Noto Sans KR. Rendered alongside, never instead. */
  titleKo: string;
  variant?: CalloutVariant;
  children: ReactNode;
  /** Korean-language body, shown under a hairline rule when present. */
  childrenKo?: ReactNode;
  className?: string;
}

export default function BilingualCallout({
  title,
  titleKo,
  variant = 'info',
  children,
  childrenKo,
  className,
}: BilingualCalloutProps) {
  const v = VARIANTS[variant];
  const Icon = v.icon;

  return (
    <aside
      className={twMerge(
        'my-8 overflow-hidden rounded-xl border shadow-sm',
        v.wrap,
        className
      )}
    >
      {/* Obangsaek edge — 4px rule keyed to the callout's semantic colour */}
      <div className={twMerge('h-1 w-full', v.rule)} />

      <div className="p-5">
        <div className="mb-3 flex items-start gap-3">
          <span
            className={twMerge(
              'mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
              v.chip
            )}
          >
            <Icon className="h-4 w-4" aria-hidden="true" />
          </span>

          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400">
                {v.label}
              </span>
              <span className="font-korean text-xs text-ink-400 dark:text-ink-500">{v.labelKo}</span>
            </div>

            {/* Dual-language header: Latin display face + Korean face, same block */}
            <h4 className="mt-1 text-lg font-bold leading-snug text-ink-900 dark:text-ink-50">
              {title}
            </h4>
            <p
              lang="ko"
              className="mt-0.5 font-korean text-sm font-medium leading-relaxed text-ink-500 dark:text-ink-400"
            >
              {titleKo}
            </p>
          </div>
        </div>

        <div className="text-[15px] leading-7 text-ink-700 dark:text-ink-200 [&>p:last-child]:mb-0 [&>p]:mb-3">
          {children}
        </div>

        {childrenKo && (
          <div className="mt-4 border-t border-ink-200/70 pt-4 dark:border-surface-dark-border">
            <div
              lang="ko"
              className="font-korean text-[15px] leading-7 text-ink-500 dark:text-ink-400 [&>p:last-child]:mb-0 [&>p]:mb-3"
            >
              {childrenKo}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
